import { type JSX, useEffect, useMemo, useState } from "react";
import SampleExplorer from "@via/pages/sample-trackers/samples/sample-explorer";
import { useAuth } from "@via/hooks/useAuth";
import {
  useVmetaCanvasesHasFileCollection,
  useVmetaProjects,
} from "@via/pages/projects/projects.api";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import { formatSharedTrackerOptionLabel } from "@via/pages/data/utils/shared-tracker-option-label";
import { uniqBy } from "@via/utils/helpers.tsx";
import { SelectOption } from "@via/utils/interfaces";
import { type VmetaProject, UserRole } from "common";

const toTrackerOption = (p: VmetaProject): SelectOption => {
  const owner =
    typeof p.owner === "object" && p.owner !== null ? p.owner : null;
  const ownerName = owner?.name ?? owner?.username ?? owner?.email ?? "";
  return {
    value: String(p._id),
    label: String(p.label ?? p.name ?? p._id),
    metadata: ownerName ? { owner: String(ownerName) } : undefined,
  };
};

export default function ActivitySampleExplorer(): JSX.Element {
  const { user } = useAuth();
  const mongoUserId =
    user && typeof user === "object" ? user?.vmeta?.userId : undefined;
  const isAdmin =
    user && typeof user === "object" ? user.userRole === UserRole.Admin : false;

  // My + Shared trackers (default scope)
  const { data: myProjects, isLoading: isMyProjectsLoading } = useVmetaProjects(
    0,
    1000,
    [],
    [],
    !!mongoUserId,
    SampleTrackerViewType.My,
    mongoUserId
  );
  const { data: sharedProjects, isLoading: isSharedProjectsLoading } =
    useVmetaProjects(
      0,
      1000,
      [],
      [],
      !!mongoUserId,
      SampleTrackerViewType.Shared,
      mongoUserId
    );

  // Admin: all trackers (explicitly opt-in via second dropdown)
  const { data: allProjects, isLoading: isAllProjectsLoading } =
    useVmetaProjects(0, 3000, [], [], isAdmin);

  // My and Shared are two independent requests. Asking which canvases hold a
  // readable file collection before both have answered starts a request for a
  // half built id list, and the answer to that one is thrown away the moment
  // the other list arrives. Wait for both.
  const isDefaultScopeLoading = isMyProjectsLoading || isSharedProjectsLoading;

  const defaultCandidateOptions: SelectOption[] = useMemo(() => {
    const my = ((myProjects?.data ?? []) as VmetaProject[]).map(
      toTrackerOption
    );
    const shared = ((sharedProjects?.data ?? []) as VmetaProject[]).map(
      toTrackerOption
    );
    return uniqBy([...my, ...shared], o => o.value).sort((a, b) =>
      String(a.label ?? "").localeCompare(String(b.label ?? ""))
    );
  }, [myProjects?.data, sharedProjects?.data]);

  const adminCandidateOptions: SelectOption[] = useMemo(() => {
    if (!isAdmin) return [];
    return uniqBy(
      ((allProjects?.data ?? []) as VmetaProject[]).map(toTrackerOption),
      o => o.value
    ).sort((a, b) =>
      String(a.label ?? "").localeCompare(String(b.label ?? ""))
    );
  }, [isAdmin, allProjects?.data]);

  const defaultCandidateIds = useMemo(
    () =>
      isDefaultScopeLoading
        ? []
        : defaultCandidateOptions
            .map(o => String(o.value ?? ""))
            .filter(Boolean),
    [defaultCandidateOptions, isDefaultScopeLoading]
  );
  const adminCandidateIds = useMemo(
    () =>
      isAllProjectsLoading
        ? []
        : adminCandidateOptions.map(o => String(o.value ?? "")).filter(Boolean),
    [adminCandidateOptions, isAllProjectsLoading]
  );

  const { data: defaultAllowed } = useVmetaCanvasesHasFileCollection(
    defaultCandidateIds,
    defaultCandidateIds.length > 0
  );
  const { data: adminAllowed } = useVmetaCanvasesHasFileCollection(
    adminCandidateIds,
    isAdmin && adminCandidateIds.length > 0
  );

  // Working out the scope is a waterfall: the My and Shared tracker lists first, then
  // hasFileCollection over their ids, and only then does the sample query have a
  // canvas list to run against. It is disabled for that whole stretch, and a disabled
  // query reports isLoading false, so the table has to be told to keep waiting or it
  // renders its "no data" message over a scope that is still on its way.
  const isScopeLoading =
    isDefaultScopeLoading ||
    (defaultCandidateIds.length > 0 && defaultAllowed === undefined);
  const isAdminScopeLoading =
    isAdmin &&
    (isAllProjectsLoading ||
      (adminCandidateIds.length > 0 && adminAllowed === undefined));

  const defaultAllowedSet = useMemo(() => {
    const ids = defaultAllowed?.data?.allowedCanvasIds ?? [];
    return new Set(ids.map(String));
  }, [defaultAllowed?.data?.allowedCanvasIds]);
  const adminAllowedSet = useMemo(() => {
    const ids = adminAllowed?.data?.allowedCanvasIds ?? [];
    return new Set(ids.map(String));
  }, [adminAllowed?.data?.allowedCanvasIds]);

  const defaultScopeOptions: SelectOption[] = useMemo(() => {
    if (defaultAllowedSet.size === 0) return [];
    return defaultCandidateOptions.filter(o =>
      defaultAllowedSet.has(String(o.value ?? ""))
    );
  }, [defaultCandidateOptions, defaultAllowedSet]);

  const defaultScopeIdSet = useMemo(() => {
    return new Set(
      defaultScopeOptions.map(o => String(o.value ?? "")).filter(Boolean)
    );
  }, [defaultScopeOptions]);

  const adminAllOptions: SelectOption[] = useMemo(() => {
    if (!isAdmin) return [];
    if (adminAllowedSet.size === 0) return [];
    return adminCandidateOptions.filter(
      o =>
        adminAllowedSet.has(String(o.value ?? "")) &&
        !defaultScopeIdSet.has(String(o.value ?? ""))
    );
  }, [isAdmin, adminCandidateOptions, adminAllowedSet, defaultScopeIdSet]);

  const [defaultSelected, setDefaultSelected] = useState<SelectOption[]>([]);
  const [adminSelected, setAdminSelected] = useState<SelectOption[]>([]);

  // Keep manual selections valid when options change (empty means "all default scope").
  useEffect(() => {
    if (defaultSelected.length === 0) return;
    const allowed = new Set(
      defaultScopeOptions.map(o => String(o.value ?? ""))
    );
    setDefaultSelected(prev =>
      prev.filter(o => allowed.has(String(o.value ?? "")))
    );
  }, [defaultScopeOptions, defaultSelected.length]);

  useEffect(() => {
    if (adminSelected.length === 0) return;
    const allowed = new Set(adminAllOptions.map(o => String(o.value ?? "")));
    setAdminSelected(prev =>
      prev.filter(o => allowed.has(String(o.value ?? "")))
    );
  }, [adminAllOptions, adminSelected.length]);

  const effectiveCanvasIds = useMemo(() => {
    const base =
      defaultSelected.length > 0 ? defaultSelected : defaultScopeOptions;
    const merged = uniqBy([...base, ...adminSelected], o => o.value);
    return merged.map(o => String(o.value ?? "")).filter(Boolean);
  }, [defaultSelected, defaultScopeOptions, adminSelected]);

  return (
    <SampleExplorer
      canvasIds={effectiveCanvasIds}
      defaultPageSize={10}
      showSavedViewsControls={false}
      isScopeLoading={isScopeLoading}
      trackerSelection={{
        label: "Sample Trackers",
        placeholder: "All My + Shared (default)",
        options: defaultScopeOptions,
        value: defaultSelected,
        onChange: setDefaultSelected,
        isLoading: isScopeLoading,
      }}
      adminTrackerSelection={
        isAdmin
          ? {
              label: "Admin",
              helperText:
                "These trackers are never auto-selected. Add them only if you want to search them.",
              placeholder: "Select additional Sample Trackers",
              options: adminAllOptions,
              value: adminSelected,
              onChange: setAdminSelected,
              isLoading: isAdminScopeLoading,
              formatOptionLabel: (option, ctx) =>
                formatSharedTrackerOptionLabel(
                  option,
                  ctx,
                  SampleTrackerViewType.Shared
                ),
            }
          : undefined
      }
    />
  );
}
